import { Component, Injector, OnDestroy, OnInit } from '@angular/core';
import {
  IonButton,
  IonButtons,
  IonHeader,
  IonMenuToggle,
  IonTitle,
  IonToolbar,
  IonIcon,
  IonContent,
  IonItem,
  IonLabel,
  IonList,
  IonNote,
  Platform,
  AlertController,
  IonListHeader,
  NavController,
} from '@ionic/angular/standalone';
import { RolePermissionService } from '../../providers/role-permission.service';
import { UserService } from '../../providers/user.service';
import { AppPagesBase } from '../../app.pages.base';
import { AuthImpersonationService } from '../../providers/auth-impersonation/auth-impersonation.service';
import { Router, RouterLink, RouterLinkActive } from '@angular/router';
import { CommonModule } from '@angular/common';
import { UserRole } from '../../enums/user-role.enum';
import { DashboardService } from './../../providers/dashboard.service';
import { NativeAuthService } from '../../providers/native-auth.service';
import Keycloak from 'keycloak-js';
import { inject } from '@angular/core';
import { CarryService } from '../../providers/carry.service';
import { MenuService } from '../../providers/menu.service';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';

interface AccountItem {
  title: string;
  url: string;
  icon: string;
  action?: string;
  email?: string;
}
@Component({
  selector: 'app-menu-item',
  imports: [
    IonHeader,
    IonToolbar,
    IonTitle,
    IonButtons,
    IonMenuToggle,
    IonButton,
    IonIcon,
    IonContent,
    IonList,
    IonItem,
    IonLabel,
    IonNote,
    RouterLink,
    RouterLinkActive,
    IonListHeader,
    CommonModule,
    TranslateModule
  ],
  templateUrl: './menu-item.page.html',
  styleUrl: './menu-item.page.scss'
})
export class menuItemPage extends AppPagesBase implements OnInit, OnDestroy {
  userRole = '';
  userEmail = '';
  menuItems = [];
  accountMenuItems: AccountItem[] = [];
  private readonly endSessionRoute = ['/distributors'];
  adminPages: AccountItem[] = [
    {
      title: 'Organizations',
      url: '/endclients',
      icon: 'business',
    }
  ];
  displayAccounInfo = false;
  private authStateSub?: Subscription;
  private menuRefreshSub?: Subscription;
  private readonly keycloak = inject(Keycloak, { optional: true });

  constructor(
    private rolePermission: RolePermissionService,
    private nativeAuthService: NativeAuthService,
    private userService: UserService,
    private authImpersonationService: AuthImpersonationService,
    private router: Router,
    private injector: Injector,
    private navCtrl: NavController,
    private carryService: CarryService,
    private menuService: MenuService,
    private translate: TranslateService,
    platform: Platform,
    alertController: AlertController,
  ) {
    super(alertController, platform);
  }

  ngOnInit() {
    this.authStateSub = this.authImpersonationService.state.subscribe(() => {
      this.getCurrentUser();
    });

    this.menuRefreshSub = this.menuService.refreshMenuObservable.subscribe(() => {
      this.refreshAccountMenuItems();
      this.refreshAdminMenuItems();
    });
  }

  ngOnDestroy() {
    this.authStateSub?.unsubscribe();
    this.menuRefreshSub?.unsubscribe();
  }

  ionViewWillEnter() {
    this.getCurrentUser();
  }

  getCurrentUser() {
    this.userService.getUserRoleFromApi().subscribe(role => {
      const userRole = role.userRole.id;
      this.rolePermission.setCurrentRole(userRole);
      this.userRole = userRole;

      this.menuItems = this.rolePermission.getFilteredMenuItems(this.platformType, 'menuItems');
      this.adminPages = this.rolePermission.getFilteredMenuItems(this.platformType, 'adminPages');
      this.refreshAccountMenuItems();
      this.refreshAdminMenuItems();
      window.localStorage.setItem('ClientId', role.client.id);
      this.userService.setLocalUserRole(this.userRole);
    });
  }

  public refreshAccountMenuItems() {
    const originalMenu = this.rolePermission.getFilteredMenuItems(this.platformType, 'account');
    const switchAccountItem = originalMenu.find(item => item.url === '/switch-account');

    const baseMenu = this.platformType === 'web'
      ? originalMenu.filter(item => item.url !== '/switch-account')
      : originalMenu;

    // For web we keep the filtered list as-is (no end-session shown there)
    if (!this.authImpersonationService.isImpersonating() || this.platformType === 'web') {
      this.accountMenuItems = baseMenu;
      return;
    }

  const impersonationState = this.authImpersonationService.getStateValue();
  const userEmail = impersonationState?.impersonatedUserEmail || window.localStorage.getItem('email');

    const endSessionTemplate = switchAccountItem ? {
      ...switchAccountItem,
      title: this.translate.instant('pages.menu.endSession'),
      url: this.endSessionRoute[0],
      action: 'endSession',
      icon: 'swap-horizontal',
      email: userEmail
    } : {
      title: this.translate.instant('pages.menu.endSession'),
      url: this.endSessionRoute[0],
      icon: 'swap-horizontal',
      action: 'endSession',
      email: userEmail
    };

    const hasSwitchInMenu = baseMenu.some(item => item.url === '/switch-account');
    this.accountMenuItems = hasSwitchInMenu
      ? baseMenu.map(item => item.url === '/switch-account' ? endSessionTemplate : item)
      : [...baseMenu, endSessionTemplate];
  }

  public refreshAdminMenuItems() {
    const originalMenu = this.rolePermission.getFilteredMenuItems(this.platformType, 'adminPages');
    // Only web has switch-account in admin section; return early for mobile
    if (this.platformType !== 'web') {
      this.adminPages = originalMenu;
      return;
    }

    if (!this.authImpersonationService.isImpersonating()) {
      this.adminPages = originalMenu;
      return;
    }

  const impersonationState = this.authImpersonationService.getStateValue();
  const userEmail = impersonationState?.impersonatedUserEmail || window.localStorage.getItem('email');

    const switchAccountItem = originalMenu.find(item => item.url === '/switch-account');
    const endSessionTemplate = switchAccountItem ? {
      ...switchAccountItem,
      title: this.translate.instant('pages.menu.endSession'),
      url: this.endSessionRoute[0],
      action: 'endSession',
      icon: 'swap-horizontal',
      email: userEmail
    } : {
      title: this.translate.instant('pages.menu.endSession'),
      url: this.endSessionRoute[0],
      icon: 'swap-horizontal',
      action: 'endSession',
      email: userEmail
    };

    const hasSwitchInMenu = originalMenu.some(item => item.url === '/switch-account');
    this.adminPages = hasSwitchInMenu
      ? originalMenu.map(item => item.url === '/switch-account' ? endSessionTemplate : item)
      : [...originalMenu, endSessionTemplate];
  }

  menuAction(item: AccountItem) {
    if (item.action === 'endSession') {
      this.confirmEndSession();
    }
    else {
      this.router.navigate([item.url]);
    }
  }

  onAdminMenuClick(item: AccountItem, event: Event) {
    if (item.action === 'endSession') {
      event.preventDefault();
      event.stopPropagation();
      this.confirmEndSession();
    }
  }

  async confirmEndSession() {
    const impersonationState = this.authImpersonationService.getStateValue();
    this.userEmail = impersonationState?.impersonatedUserEmail || window.localStorage.getItem('email');
    const alert = await this.alertController.create({
      header: this.translate.instant('pages.menu.confirmTitle'),
      message: this.translate.instant('pages.menu.endSessionConfirm', { email: this.userEmail }),
      buttons: [
        {
          text: this.translate.instant('pages.menu.cancel'),
          role: 'cancel'
        },
        {
          text: this.translate.instant('pages.menu.ok'),
          handler: () => {
            this.authImpersonationService.exitImpersonation();
            this.rolePermission.setCurrentRole(UserRole.VIVENT_ADMIN);
            const targetRoute = this.endSessionRoute;
            const currentUrl = this.router.url;
            if (currentUrl === '/dashboard') {
              const dashboardService = this.injector.get(DashboardService);
              dashboardService.triggerRefresh();
            } else {
              this.router.navigate(targetRoute);
            }
            this.refreshAccountMenuItems();
            this.refreshAdminMenuItems();
          }
        }
      ]
    });

    await alert.present();
  }

  async logout() {
    const alert = await this.alertController.create({
      header: this.translate.instant('pages.menu.logoutTitle'),
      message: this.translate.instant('pages.menu.logoutMessage', { email: this.userEmail }),
      buttons: [
        {
          text: this.translate.instant('pages.menu.cancel'),
          role: 'cancel'
        },
        {
          text: this.translate.instant('pages.menu.ok'),
          handler: async () => {
            this.userService.logout();
            this.authImpersonationService.exitImpersonation();
            window.localStorage.removeItem('userRole');
            window.localStorage.removeItem('ClientId');
            window.localStorage.removeItem('email');
            window.localStorage.removeItem('userId');

            try {
              if (this.keycloak) {
                await this.keycloak.logout({ redirectUri: `${window.location.origin}/#/login` });
              } else {
                await this.nativeAuthService.logout();
              }
            } catch (error) {
              console.error('Logout failed', error);
            }

            this.logoutAllSessions();
          }
        }
      ]
    });

    await alert.present();
  }

  logoutAllSessions() {
    this.carryService.triggerRefresh();
    this.navCtrl.navigateRoot('/login');
  }
}
